import { apiEndpoints } from "@config/env";
import { getData } from "../../../utils/global";
import { debug as logDebug, error as logError } from "../../../utils/console";
import { persistantStore } from "../../../store/persistantStore";
import { SPEED_UNIT_KPH, SPEED_UNIT_KTS, normalizeSpeedDisplayUnit, twsManeuverBandHalf, type SpeedDisplayUnit } from "../../../utils/speedUnits";

export type DailyTrainingSettingsView = "dataset" | "day" | "fleet";
export type DailyTrainingLegType = "upwind" | "downwind" | "reaching";
export type DailyTrainingManeuverType = "tack" | "gybe" | "roundup" | "bearaway";

export interface DailyTrainingScatterPoint {
  event_id: number;
  start_time: string;
  source_name: string;
  tws_bin: number;
  tws?: number | null;
}

export interface DailyTrainingSettingsPayload {
  straight_line_rows: Record<string, unknown>[];
  maneuver_rows: Record<string, unknown>[];
  acceleration_rows: Record<string, unknown>[];
  tws_bins: number[];
  scatter_points: DailyTrainingScatterPoint[];
}

export interface DailyTrainingCoveragePoint {
  calendar_date?: string;
  event_id: number;
  start_time: string;
  source_name: string;
  tws_bin: number;
  tws?: number | null;
  /** Signed segment TWA, so a consumer can colour the marker by tack. */
  twa_signed?: number | null;
  segment_count?: number;
}

export interface DailyTrainingCoveragePayload {
  coverage_points: DailyTrainingCoveragePoint[];
  tws_bins: number[];
}

/** History filters for fleet Summary Tables (same shape as maneuver history API). */
export type SummaryTablesHistoryFilters = Record<string, (number | string)[]>;

/** Which table sections to load: both (default), straight, or maneuvers. */
export type DailyTrainingSettingsSections = "both" | "straight" | "maneuvers";

export interface FetchDailyTrainingSettingsParams {
  className: string;
  projectId: string | number;
  dateYmd?: string;
  view: DailyTrainingSettingsView;
  legType: DailyTrainingLegType;
  maneuverType: DailyTrainingManeuverType;
  speedUnit: "kts" | "kph";
  twsBin?: number | null;
  /** Comma-separated TWS bin centers for day view / timeline-filtered tables. */
  twsBins?: number[] | null;
  /** BIN 10 dataset_events.event_id values from timeline brush (straight-line rows, day view only). */
  eventIds?: number[] | null;
  /** Timeline brush range — fleet filters both tables; day view filters maneuvers only. */
  brushStartTime?: string | null;
  brushEndTime?: string | null;
  sourceId?: number | null;
  sourceNames?: string[];
  datasetId?: number | null;
  /** Fleet history date range (YYYYMMDD). */
  startDate?: string | null;
  endDate?: string | null;
  filters?: SummaryTablesHistoryFilters | null;
  /** When "maneuvers", only the maneuver query runs (maneuver type changes). */
  sections?: DailyTrainingSettingsSections;
  /** Race number filter (Race Summary day view). "All"/empty = all races. */
  race?: string | null;
  /** Fleet live view: restrict to LIVE datasets (report_name='LIVE'). */
  liveOnly?: boolean;
  signal?: AbortSignal;
}

export interface FetchDailyTrainingCoverageTimelineParams {
  className: string;
  projectId: string | number;
  legType: DailyTrainingLegType;
  speedUnit: "kts" | "kph";
  twsBin?: number | null;
  sourceId?: number | null;
  sourceNames?: string[];
  startDate?: string | null;
  endDate?: string | null;
  filters?: SummaryTablesHistoryFilters | null;
  /** Fleet live view: restrict to LIVE datasets (report_name='LIVE'). */
  liveOnly?: boolean;
  signal?: AbortSignal;
}

/** Build filters JSON for fleet Summary Tables from page filter state. */
export function buildSummaryTablesHistoryFilters(opts: {
  selectedGrades: number[];
  selectedStates: string[];
  filterYear: string;
  filterEvent: string;
  filterConfig: string;
  trainingRacing: "TRAINING" | "RACING" | null;
}): SummaryTablesHistoryFilters {
  const filters: SummaryTablesHistoryFilters = {};

  const apiGradeValues = opts.selectedGrades
    .map((g) => Number(g))
    .filter((n) => Number.isInteger(n) && n >= 0 && n <= 5);
  if (apiGradeValues.length > 0) {
    filters.GRADE = apiGradeValues;
  }

  if (opts.filterYear.trim()) {
    const years = opts.filterYear
      .split(",")
      .map((y) => parseInt(y.trim(), 10))
      .filter((y) => !Number.isNaN(y));
    if (years.length > 0) {filters.YEAR = years;}
  }

  if (opts.filterEvent.trim()) {
    const events = opts.filterEvent
      .split(",")
      .map((e) => e.trim())
      .filter((e) => e.length > 0);
    if (events.length > 0) {filters.EVENT = events;}
  }

  if (opts.filterConfig.trim()) {
    const configs = opts.filterConfig
      .split(",")
      .map((c) => c.trim())
      .filter((c) => c.length > 0);
    if (configs.length > 0) {filters.CONFIG = configs;}
  }

  if (opts.selectedStates.length > 0) {
    filters.STATE = [...opts.selectedStates];
  }

  if (opts.trainingRacing != null) {
    filters.TRAINING_RACING = [opts.trainingRacing];
  }

  return filters;
}

function formatYmdForDisplay(ymd: string | null | undefined): string {
  const n = normalizeDateYmd(ymd);
  if (n.length !== 8) {
    return String(ymd ?? "").trim();
  }
  return `${n.slice(0, 4)}-${n.slice(4, 6)}-${n.slice(6, 8)}`;
}

export function formatBrushDateRange(start: Date, end: Date): string {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  return `${fmt(start)}–${fmt(end)}`;
}

export type SummaryTablesAppliedFiltersHintOpts = {
  startDate?: string;
  endDate?: string;
  sourceNames?: string[];
  totalSourceCount?: number;
  filters?: SummaryTablesHistoryFilters | null;
  legType?: DailyTrainingLegType;
  maneuverType?: DailyTrainingManeuverType;
  brushedTimeRange?: { start: Date; end: Date } | null;
  twsBin?: number | null;
  twsDisplayUnit?: SpeedDisplayUnit;
};

/** Human-readable filters currently applied to fleet Summary Tables data. */
export function summaryTablesAppliedFilterParts(
  opts: SummaryTablesAppliedFiltersHintOpts
): string[] {
  const parts: string[] = [];

  if (opts.legType) {
    const legLabel =
      opts.legType === "reaching" ? "Reaching" : opts.legType === "downwind" ? "Downwind" : "Upwind";
    parts.push(`Point of sail: ${legLabel}`);
  }

  if (opts.maneuverType) {
    const m = opts.maneuverType;
    const maneuverLabel = m.charAt(0).toUpperCase() + m.slice(1);
    parts.push(`Maneuver: ${maneuverLabel}`);
  }

  if (opts.twsBin != null && !Number.isNaN(Number(opts.twsBin))) {
    const bin = Number(opts.twsBin);
    const unit = opts.twsDisplayUnit ?? SPEED_UNIT_KPH;
    parts.push(`TWS bin: ${bin} (${formatDailySettingsTwsBinRangeHint(bin, unit)})`);
  }

  const start = formatYmdForDisplay(opts.startDate);
  const end = formatYmdForDisplay(opts.endDate);
  if (start && end) {
    parts.push(`Dates: ${start}–${end}`);
  }

  const sources = (opts.sourceNames ?? []).map((s) => String(s).trim()).filter(Boolean);
  const totalSources = opts.totalSourceCount ?? 0;
  if (sources.length > 0 && (totalSources === 0 || sources.length < totalSources)) {
    parts.push(
      sources.length <= 5 ? `Boats: ${sources.join(", ")}` : `Boats: ${sources.length} selected`
    );
  }

  const f = opts.filters ?? {};

  if (Array.isArray(f.GRADE) && f.GRADE.length > 0) {
    const gradeValues = f.GRADE.map((g) => Number(g)).filter((n) => Number.isInteger(n) && n >= -1);
    if (gradeValues.length > 0) {
      const minGrade = Math.max(...gradeValues);
      const effectiveMin = Math.max(minGrade, 1);
      parts.push(`Grade: > ${effectiveMin}`);
    }
  } else if (opts.legType === "reaching") {
    parts.push("Grade: ≥ 1");
  } else {
    parts.push("Grade: > 1");
  }

  if (Array.isArray(f.STATE) && f.STATE.length > 0) {
    parts.push(`Foiling state: ${f.STATE.map(String).join(", ")}`);
  }

  if (Array.isArray(f.YEAR) && f.YEAR.length > 0) {
    parts.push(`Year: ${f.YEAR.map(String).join(", ")}`);
  }

  if (Array.isArray(f.EVENT) && f.EVENT.length > 0) {
    parts.push(`Event: ${f.EVENT.map(String).join(", ")}`);
  }

  if (Array.isArray(f.CONFIG) && f.CONFIG.length > 0) {
    parts.push(`Config: ${f.CONFIG.map(String).join(", ")}`);
  }

  if (Array.isArray(f.TRAINING_RACING) && f.TRAINING_RACING.length > 0) {
    const tr = String(f.TRAINING_RACING[0]).trim().toUpperCase();
    if (tr === "TRAINING") {
      parts.push("Sessions: training only");
    } else if (tr === "RACING") {
      parts.push("Sessions: racing only");
    }
  }

  if (opts.brushedTimeRange) {
    parts.push(
      `Timeline brush: ${formatBrushDateRange(opts.brushedTimeRange.start, opts.brushedTimeRange.end)}`
    );
  }

  if (parts.length === 0) {
    return [];
  }
  return parts;
}

/** Human-readable summary of filters currently applied to fleet Summary Tables data. */
export function formatSummaryTablesAppliedFiltersHint(
  opts: SummaryTablesAppliedFiltersHintOpts
): string {
  const parts = summaryTablesAppliedFilterParts(opts);
  if (parts.length === 0) {
    return "";
  }
  return ` Applied filters: ${parts.join("; ")}.`;
}

function normalizeDateYmd(date: string | null | undefined): string {
  if (date == null || String(date).trim() === "") {return "";}
  return String(date).replace(/[-/]/g, "");
}

export function fleetDateRangeReady(startDate?: string | null, endDate?: string | null): boolean {
  return normalizeDateYmd(startDate).length === 8 && normalizeDateYmd(endDate).length === 8;
}

export function normalizeDailySettingsRows(raw: unknown[]): Record<string, unknown>[] {
  return raw.map((r) => {
    const row = (r && typeof r === "object" ? r : {}) as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(row)) {
      out[k.toLowerCase()] = row[k];
    }
    return out;
  });
}

/** Dropdown label: bin center only (e.g. "10", "12"). */
export function formatDailySettingsTwsBinDropdownLabel(center: number): string {
  return String(center);
}

/** Hint under TWS dropdown: band used for filtering (e.g. "Range: 9–11 kts" for center 10 in kts). */
export function formatDailySettingsTwsBinRangeHint(center: number, displayUnit: SpeedDisplayUnit): string {
  const half = twsManeuverBandHalf(displayUnit);
  const lo = center - half;
  const hi = center + half;
  const u = displayUnit === SPEED_UNIT_KTS ? "kts" : "kph";
  const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));
  return `Range: ${fmt(lo)}-${fmt(hi)} ${u}`;
}

export function twsBinCenterFromDropdownLabel(label: string): number | null {
  const m = /^(\d+)/.exec(String(label).trim());
  if (!m) {return null;}
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
}

/** TWS bin center from a normalized settings row (tws or tws_bin column). */
export function twsBinFromDailySettingsRow(row: Record<string, unknown>): number | null {
  const v = row.tws ?? row.tws_bin;
  if (v == null || v === "") {return null;}
  const n = Number(v);
  return Number.isNaN(n) ? null : Math.round(n);
}

/** Day/fleet view: keep rows for the selected TWS bin when rows carry a bin column.
 *  When rows are one-per-source (no tws/tws_bin), the API already filtered by bin — return as-is. */
export function filterDailyTrainingRowsByTwsBin(
  rows: Record<string, unknown>[],
  selectedBin: number | null | undefined
): Record<string, unknown>[] {
  if (selectedBin == null || Number.isNaN(selectedBin)) {return rows;}
  if (rows.length === 0) {return rows;}
  const hasPerRowBins = rows.some((row) => twsBinFromDailySettingsRow(row) != null);
  if (!hasPerRowBins) {return rows;}
  const target = Math.round(selectedBin);
  return rows.filter((row) => twsBinFromDailySettingsRow(row) === target);
}

/** Dataset view: keep rows whose TWS bin is in the selected set (all bins when set is empty). */
export function filterDailyTrainingRowsByTwsBins(
  rows: Record<string, unknown>[],
  selectedBins: number[] | null | undefined
): Record<string, unknown>[] {
  if (selectedBins == null || selectedBins.length === 0) {return rows;}
  const targets = new Set(selectedBins.map((b) => Math.round(b)));
  return rows.filter((row) => {
    const bin = twsBinFromDailySettingsRow(row);
    return bin != null && targets.has(bin);
  });
}

export function normalizeDailyTrainingCoveragePoints(raw: unknown[]): DailyTrainingCoveragePoint[] {
  const out: DailyTrainingCoveragePoint[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {continue;}
    const r = item as Record<string, unknown>;
    const eventId = Number(r.event_id);
    const twsBin = Number(r.tws_bin ?? r.tws);
    if (Number.isNaN(eventId) || Number.isNaN(twsBin)) {continue;}
    const start = r.start_time;
    if (start == null || String(start).trim() === "") {continue;}
    out.push({
      calendar_date: r.calendar_date != null ? String(r.calendar_date) : undefined,
      event_id: eventId,
      start_time: String(start),
      source_name: String(r.source_name ?? ""),
      tws_bin: Math.round(twsBin),
      tws: r.tws != null ? Number(r.tws) : null,
      // Signed segment TWA, so a consumer can colour by tack. This normalizer rebuilds each
      // point field by field, so anything not listed here is silently dropped on the way in.
      twa_signed:
        r.twa_signed != null && !Number.isNaN(Number(r.twa_signed)) ? Number(r.twa_signed) : null,
      segment_count: r.segment_count != null ? Number(r.segment_count) : undefined,
    });
  }
  return out;
}

/** Map fleet coverage markers to ScatterTimeseries points (time × TWS). */
export function mapCoveragePointsToTimelinePoints(
  points: DailyTrainingCoveragePoint[],
  twsDisplayUnit?: SpeedDisplayUnit
): Record<string, unknown>[] {
  return mapTrainingScatterToTimelinePoints(points as DailyTrainingScatterPoint[], twsDisplayUnit);
}

export function normalizeDailyTrainingScatterPoints(raw: unknown[]): DailyTrainingScatterPoint[] {
  const out: DailyTrainingScatterPoint[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {continue;}
    const r = item as Record<string, unknown>;
    const eventId = Number(r.event_id);
    const twsBin = Number(r.tws_bin ?? r.tws);
    if (Number.isNaN(eventId) || Number.isNaN(twsBin)) {continue;}
    const start = r.start_time;
    if (start == null || String(start).trim() === "") {continue;}
    out.push({
      event_id: eventId,
      start_time: String(start),
      source_name: String(r.source_name ?? ""),
      tws_bin: Math.round(twsBin),
      tws: r.tws != null ? Number(r.tws) : null,
    });
  }
  return out;
}

/** Map BIN 10 scatter rows to ScatterTimeseries points (time × TWS). */
export function mapTrainingScatterToTimelinePoints(
  points: DailyTrainingScatterPoint[],
  twsDisplayUnit?: SpeedDisplayUnit
): Record<string, unknown>[] {
  if (points.length === 0) {return [];}
  const unit =
    twsDisplayUnit ?? normalizeSpeedDisplayUnit(persistantStore.defaultUnits());
  const twsChannel = `Tws_${unit === SPEED_UNIT_KTS ? "kts" : "kph"}`;
  const twsLower = twsChannel.toLowerCase();
  const out: Record<string, unknown>[] = [];
  for (const p of points) {
    const instant = new Date(String(p.start_time).replace(/ /g, "T"));
    if (Number.isNaN(instant.getTime())) {continue;}
    const y = p.tws != null && !Number.isNaN(Number(p.tws)) ? Number(p.tws) : p.tws_bin;
    // Tack comes from the sign of the segment's TWA, when the query supplied it. Additive:
    // pages colouring by SOURCE never look at these fields.
    const twaSigned =
      (p as DailyTrainingCoveragePoint).twa_signed != null &&
      !Number.isNaN(Number((p as DailyTrainingCoveragePoint).twa_signed))
        ? Number((p as DailyTrainingCoveragePoint).twa_signed)
        : null;
    const row: Record<string, unknown> = {
      event_id: p.event_id,
      source_name: p.source_name,
      tws_bin: p.tws_bin,
      Datetime: instant,
      datetime: instant,
      event_start_time: p.start_time,
      [twsChannel]: y,
      [twsLower]: y,
      TWS: y,
    };
    if (twaSigned !== null && twaSigned !== 0) {
      row.Twa_deg = twaSigned;
      row.Twa_signed = twaSigned;
      row.TACK = twaSigned < 0 ? "PORT" : "STBD";
    }
    out.push(row);
  }
  return out;
}

/** Unique TWS bin centers for points whose event_id is in the brushed set. */
export function twsBinsFromBrushedTrainingPoints(
  points: DailyTrainingScatterPoint[],
  eventIds: number[]
): number[] {
  if (eventIds.length === 0) {return [];}
  const idSet = new Set(eventIds);
  const bins = new Set<number>();
  for (const p of points) {
    if (idSet.has(p.event_id)) {bins.add(p.tws_bin);}
  }
  return [...bins].sort((a, b) => a - b);
}

/** Day view: one row per team/source. */
export function dedupeDailyTrainingRowsBySource(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const seen = new Set<string>();
  for (const row of rows) {
    const name = String(row.source_name ?? "")
      .trim()
      .toLowerCase();
    if (!name) {
      out.push(row);
      continue;
    }
    if (seen.has(name)) {continue;}
    seen.add(name);
    out.push(row);
  }
  return out;
}

export function filterDailyTrainingRowsForDayView(
  rows: Record<string, unknown>[],
  selectedBin: number | null | undefined
): Record<string, unknown>[] {
  return dedupeDailyTrainingRowsBySource(filterDailyTrainingRowsByTwsBin(rows, selectedBin));
}

export function filterDailyTrainingRowsForDatasetView(
  rows: Record<string, unknown>[],
  selectedBins: number[] | null | undefined
): Record<string, unknown>[] {
  return filterDailyTrainingRowsByTwsBins(rows, selectedBins);
}

export async function fetchDailyTrainingSettings(
  params: FetchDailyTrainingSettingsParams
): Promise<{ ok: true; data: DailyTrainingSettingsPayload } | { ok: false; message: string }> {
  const {
    className,
    projectId,
    dateYmd,
    view,
    legType,
    maneuverType,
    speedUnit,
    twsBin,
    twsBins,
    eventIds,
    brushStartTime,
    brushEndTime,
    sourceId,
    sourceNames,
    datasetId,
    startDate,
    endDate,
    filters,
    sections,
    race,
    liveOnly,
    signal,
  } = params;

  let url =
    `${apiEndpoints.app.data}/daily-training-settings?class_name=${encodeURIComponent(className)}` +
    `&project_id=${encodeURIComponent(String(projectId))}` +
    `&view=${encodeURIComponent(view)}` +
    `&leg_type=${encodeURIComponent(legType)}` +
    `&maneuver_type=${encodeURIComponent(maneuverType)}` +
    `&speed_unit=${encodeURIComponent(speedUnit)}`;

  if (view === "fleet") {
    const startYmd = normalizeDateYmd(startDate);
    const endYmd = normalizeDateYmd(endDate);
    if (startYmd.length !== 8 || endYmd.length !== 8) {
      return { ok: false, message: "Start and end dates are required for fleet summary tables" };
    }
    url += `&start_date=${encodeURIComponent(startYmd)}`;
    url += `&end_date=${encodeURIComponent(endYmd)}`;
    if (filters != null && Object.keys(filters).length > 0) {
      url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
    }
  } else if (dateYmd != null && dateYmd.length === 8) {
    url += `&date=${encodeURIComponent(dateYmd)}`;
  }

  if (twsBins != null && twsBins.length > 0) {
    url += `&tws_bins=${encodeURIComponent(twsBins.map(String).join(","))}`;
  } else if ((view === "day" || view === "fleet") && twsBin != null && !Number.isNaN(twsBin)) {
    url += `&tws_bin=${encodeURIComponent(String(twsBin))}`;
  }
  if (view !== "fleet" && eventIds != null && eventIds.length > 0) {
    url += `&event_ids=${encodeURIComponent(eventIds.map(String).join(","))}`;
  }
  if (brushStartTime != null && brushStartTime.trim() !== "") {
    url += `&brush_start_time=${encodeURIComponent(brushStartTime)}`;
  }
  if (brushEndTime != null && brushEndTime.trim() !== "") {
    url += `&brush_end_time=${encodeURIComponent(brushEndTime)}`;
  }
  if (datasetId != null && datasetId > 0) {
    url += `&dataset_id=${encodeURIComponent(String(datasetId))}`;
  }
  if (sourceNames != null && sourceNames.length > 0) {
    url += `&source_names=${encodeURIComponent(JSON.stringify(sourceNames))}`;
  } else if (sourceId != null && sourceId > 0) {
    url += `&source_id=${encodeURIComponent(String(sourceId))}`;
  }
  if (sections != null && sections !== "both") {
    url += `&sections=${encodeURIComponent(sections)}`;
  }
  if (race != null && String(race).trim() !== "" && String(race).trim() !== "All") {
    url += `&race=${encodeURIComponent(String(race).trim())}`;
  }
  if (liveOnly === true) {
    url += `&live_only=1`;
  }

  try {
    const result = await getData(url, signal);
    if (!result?.success) {
      return { ok: false, message: result?.message ?? "Failed to load daily training settings" };
    }
    const payload = (result.data ?? {}) as {
      straight_line_rows?: unknown[];
      maneuver_rows?: unknown[];
      acceleration_rows?: unknown[];
      tws_bins?: unknown[];
      scatter_points?: unknown[];
    };
    const straightRaw = Array.isArray(payload.straight_line_rows) ? payload.straight_line_rows : [];
    const maneuverRaw = Array.isArray(payload.maneuver_rows) ? payload.maneuver_rows : [];
    const accelerationRaw = Array.isArray(payload.acceleration_rows) ? payload.acceleration_rows : [];
    const tws_bins = (Array.isArray(payload.tws_bins) ? payload.tws_bins : [])
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v));

    const scatterRaw = Array.isArray(payload.scatter_points) ? payload.scatter_points : [];
    const data: DailyTrainingSettingsPayload = {
      straight_line_rows: normalizeDailySettingsRows(straightRaw),
      maneuver_rows: normalizeDailySettingsRows(maneuverRaw),
      acceleration_rows: normalizeDailySettingsRows(accelerationRaw),
      tws_bins,
      scatter_points: normalizeDailyTrainingScatterPoints(scatterRaw),
    };
    logDebug("dailyTrainingSettings: fetched", {
      view,
      straight: data.straight_line_rows.length,
      maneuver: data.maneuver_rows.length,
      acceleration: data.acceleration_rows.length,
      twsBins: data.tws_bins.length,
      scatter: data.scatter_points.length,
    });
    return { ok: true, data };
  } catch (err) {
    if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
      return { ok: false, message: "Request cancelled" };
    }
    logError("dailyTrainingSettings: fetch failed", err);
    return {
      ok: false,
      message: err instanceof Error ? err.message : "Failed to load daily training settings",
    };
  }
}

export async function fetchDailyTrainingCoverageTimeline(
  params: FetchDailyTrainingCoverageTimelineParams
): Promise<{ ok: true; data: DailyTrainingCoveragePayload } | { ok: false; message: string }> {
  const {
    className,
    projectId,
    legType,
    speedUnit,
    twsBin,
    sourceId,
    sourceNames,
    startDate,
    endDate,
    filters,
    liveOnly,
    signal,
  } = params;

  let url =
    `${apiEndpoints.app.data}/daily-training-settings/coverage-timeline?class_name=${encodeURIComponent(className)}` +
    `&project_id=${encodeURIComponent(String(projectId))}` +
    `&leg_type=${encodeURIComponent(legType)}` +
    `&speed_unit=${encodeURIComponent(speedUnit)}`;

  const startYmd = normalizeDateYmd(startDate);
  const endYmd = normalizeDateYmd(endDate);
  if (startYmd.length !== 8 || endYmd.length !== 8) {
    return { ok: false, message: "Start and end dates are required for fleet coverage timeline" };
  }
  url += `&start_date=${encodeURIComponent(startYmd)}`;
  url += `&end_date=${encodeURIComponent(endYmd)}`;
  if (filters != null && Object.keys(filters).length > 0) {
    url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
  }
  if (twsBin != null && !Number.isNaN(twsBin)) {
    url += `&tws_bin=${encodeURIComponent(String(twsBin))}`;
  }
  if (sourceNames != null && sourceNames.length > 0) {
    url += `&source_names=${encodeURIComponent(JSON.stringify(sourceNames))}`;
  } else if (sourceId != null && sourceId > 0) {
    url += `&source_id=${encodeURIComponent(String(sourceId))}`;
  }
  if (liveOnly === true) {
    url += `&live_only=1`;
  }

  try {
    const result = await getData(url, signal);
    if (!result?.success) {
      return { ok: false, message: result?.message ?? "Failed to load coverage timeline" };
    }
    const payload = (result.data ?? {}) as {
      coverage_points?: unknown[];
      tws_bins?: unknown[];
    };
    const coverageRaw = Array.isArray(payload.coverage_points) ? payload.coverage_points : [];
    const tws_bins = (Array.isArray(payload.tws_bins) ? payload.tws_bins : [])
      .map((v) => Number(v))
      .filter((v) => !Number.isNaN(v));
    const data: DailyTrainingCoveragePayload = {
      coverage_points: normalizeDailyTrainingCoveragePoints(coverageRaw),
      tws_bins,
    };
    logDebug("dailyTrainingSettings: coverage timeline fetched", {
      points: data.coverage_points.length,
      twsBins: data.tws_bins.length,
    });
    return { ok: true, data };
  } catch (err) {
    if (signal?.aborted || (err instanceof DOMException && err.name === "AbortError")) {
      return { ok: false, message: "Request cancelled" };
    }
    logError("dailyTrainingSettings: coverage timeline fetch failed", err);
    return {
      ok: false,
      message: err instanceof Error ? err.message : "Failed to load coverage timeline",
    };
  }
}
